import React, { useState, useRef, useEffect } from 'react';
import { CheckCircle2, X, ChevronDown, ChevronUp, UserPlus, Link, Users, BarChart2, Share2 } from 'lucide-react';
import PublicNavbar from '@/components/landing/PublicNavbar';
import PublicFooter from '@/components/landing/PublicFooter';
import { base44 } from '@/api/base44Client';
import { useI18n } from '@/lib/I18nContext';

function useIntersection(ref, threshold = 0.15) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref, threshold]);
  return visible;
}

function StepRow({ step, i, t }) {
  const ref = useRef(null);
  const visible = useIntersection(ref, 0.2);
  const isLeft = i % 2 === 0;

  function mockup(n) {
    const W = ({ children, ...p }) => <div className="rounded-2xl p-5 w-full max-w-xs mx-auto shadow-2xl" style={{ background: '#111827', border: '1px solid #1E2D45' }} {...p}>{children}</div>;
    const btn = (key) => <div className="rounded-lg px-3 py-2 text-xs text-center font-semibold" style={{ background: '#00D5FF', color: '#070B14' }}>{t(key)}</div>;
    switch (n) {
      case '01': return (
        <W>
          <p className="text-xs mb-3" style={{ color: '#475569' }}>{t('how_it_works_page.mock_create_account')}</p>
          <div className="space-y-2">
            <div className="rounded-lg px-3 py-2 text-xs" style={{ background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.6)' }}>maria@agencia.com</div>
            <div className="rounded-lg px-3 py-2 text-xs" style={{ background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.3)' }}>••••••••</div>
            {btn('how_it_works_page.mock_create_free')}
          </div>
          <p className="text-center text-[10px] mt-2" style={{ color: '#475569' }}>{t('how_it_works_page.mock_free_trial')}</p>
        </W>
      );
      case '02': return (
        <W>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold" style={{ background: '#1877F2', color: 'white' }}>f</div>
            <div>
              <p className="text-xs font-semibold" style={{ color: '#F8FAFC' }}>{t('how_it_works_page.mock_meta_business')}</p>
              <p className="text-[10px]" style={{ color: '#475569' }}>{t('how_it_works_page.mock_auth_access')}</p>
            </div>
          </div>
          <div className="rounded-lg p-2 mb-3 space-y-1.5" style={{ background: 'rgba(255,255,255,0.04)' }}>
            {[t('how_it_works_page.mock_view_campaigns'), t('how_it_works_page.mock_view_metrics'), t('how_it_works_page.mock_view_creatives')].map(p => (
              <div key={p} className="flex items-center gap-2">
                <div className="w-3.5 h-3.5 rounded-full flex items-center justify-center" style={{ background: '#22C55E' }}>
                  <CheckCircle2 className="w-2 h-2 text-white" />
                </div>
                <span className="text-[10px]" style={{ color: '#94A3B8' }}>{p}</span>
              </div>
            ))}
          </div>
          {btn('how_it_works_page.mock_authorize_meta')}
        </W>
      );
      case '03': return (
        <W>
          <p className="text-xs mb-3" style={{ color: '#475569' }}>{t('how_it_works_page.mock_my_clients')}</p>
          {[
            t('how_it_works_page.mock_client_1', 'Cliente 1'),
            t('how_it_works_page.mock_client_2', 'Cliente 2'),
            t('how_it_works_page.mock_client_3', 'Cliente 3'),
          ].map((c, ci) => (
            <div key={c} className="flex items-center gap-2.5 p-2 rounded-lg mb-1.5" style={{ background: ci === 0 ? 'rgba(0,213,255,0.1)' : 'rgba(255,255,255,0.04)', border: ci === 0 ? '1px solid rgba(0,213,255,0.3)' : '1px solid transparent' }}>
              <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-[10px] font-bold" style={{ background: 'linear-gradient(135deg, #00D5FF, #7C3AED)' }}>{c[0]}</div>
              <span className="text-xs" style={{ color: '#F8FAFC' }}>{c}</span>
              {ci === 0 && <span className="ml-auto text-[9px]" style={{ color: '#00D5FF' }}>{t('how_it_works_page.mock_active')}</span>}
            </div>
          ))}
          <div className="rounded-lg p-2 text-center text-[10px] mt-2" style={{ border: '1px dashed #1E2D45', color: '#475569' }}>{t('how_it_works_page.mock_add_client')}</div>
        </W>
      );
      case '04': return (
        <div className="rounded-2xl p-4 w-full max-w-xs mx-auto shadow-2xl" style={{ background: '#111827', border: '1px solid #1E2D45' }}>
          <div className="grid grid-cols-2 gap-2 mb-3">
            {[['impressions', '1.2M', '+12%'], ['clicks', '34.5K', '+23%'], ['cpc', '$1.20', '-5%'], ['roas', '4.8x', '+14%']].map(([key, val, delta]) => (
              <div key={key} className="rounded-lg p-2" style={{ background: 'rgba(255,255,255,0.04)' }}>
                <p className="text-[9px]" style={{ color: '#475569' }}>{t('metrics.' + key, key)}</p>
                <p className="font-bold text-sm" style={{ color: '#F8FAFC' }}>{val}</p>
                <p className="text-[9px]" style={{ color: delta.startsWith('+') ? '#22C55E' : '#EF4444' }}>{delta}</p>
              </div>
            ))}
          </div>
          <div className="rounded-lg p-2 flex items-end gap-1 h-10" style={{ background: 'rgba(255,255,255,0.04)' }}>
            {[3,5,4,7,6,8,5,9,7,8,6,7].map((h,hi) => (
              <div key={hi} className="flex-1 rounded-sm" style={{ height: `${h * 9}%`, background: 'rgba(0,213,255,0.5)' }} />
            ))}
          </div>
        </div>
      );
      case '05': return (
        <W>
          <p className="text-xs mb-3" style={{ color: '#475569' }}>{t('how_it_works_page.mock_share_report')}</p>
          <div className="space-y-2">
            <div className="rounded-lg p-3 flex items-center gap-2.5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid #1E2D45' }}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center text-sm" style={{ background: 'rgba(239,68,68,0.2)' }}>📄</div>
              <div>
                <p className="text-xs font-semibold" style={{ color: '#F8FAFC' }}>{t('how_it_works_page.mock_export_pdf')}</p>
                <p className="text-[10px]" style={{ color: '#475569' }}>{t('how_it_works_page.mock_with_agency_logo')}</p>
              </div>
            </div>
            <div className="rounded-lg p-3 flex items-center gap-2.5" style={{ background: 'rgba(0,213,255,0.08)', border: '1px solid rgba(0,213,255,0.25)' }}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center text-sm" style={{ background: 'rgba(0,213,255,0.15)' }}>🔗</div>
              <div>
                <p className="text-xs font-semibold" style={{ color: '#00D5FF' }}>{t('how_it_works_page.mock_live_link')}</p>
                <p className="text-[10px]" style={{ color: 'rgba(0,213,255,0.5)' }}>{t('how_it_works_page.mock_with_pin')}</p>
              </div>
            </div>
          </div>
        </W>
      );
      default: return null;
    }
  }

  return (
    <div
      ref={ref}
      className={`flex flex-col ${isLeft ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-10`}
      style={{
        transition: 'opacity 0.7s ease, transform 0.7s ease',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateX(0)' : `translateX(${isLeft ? '-32px' : '32px'})`,
      }}
    >
      <div className="flex-1 space-y-4">
        <div className="text-[80px] font-heading font-black leading-none select-none" style={{ color: 'rgba(0,213,255,0.06)', lineHeight: 1 }}>
          {step.n}
        </div>
        <div className="w-11 h-11 rounded-xl flex items-center justify-center -mt-6" style={{ background: 'linear-gradient(135deg, #00D5FF, #7C3AED)' }}>
          <step.icon className="w-5 h-5 text-white" />
        </div>
        <h2 className="font-heading font-bold text-2xl" style={{ color: '#F8FAFC' }}>{t(step.titleKey)}</h2>
        <p className="text-base leading-relaxed" style={{ color: '#94A3B8' }}>{t(step.descKey)}</p>
      </div>
      <div className="flex-1 flex justify-center">{mockup(step.n)}</div>
    </div>
  );
}

const STEPS = [
  { n: '01', icon: UserPlus, titleKey: 'how_it_works_page.step1_title', descKey: 'how_it_works_page.step1_desc' },
  { n: '02', icon: Link, titleKey: 'how_it_works_page.step2_title', descKey: 'how_it_works_page.step2_desc' },
  { n: '03', icon: Users, titleKey: 'how_it_works_page.step3_title', descKey: 'how_it_works_page.step3_desc' },
  { n: '04', icon: BarChart2, titleKey: 'how_it_works_page.step4_title', descKey: 'how_it_works_page.step4_desc' },
  { n: '05', icon: Share2, titleKey: 'how_it_works_page.step5_title', descKey: 'how_it_works_page.step5_desc' },
];

const COMPARISON_KEYS = [
  ['how_it_works_page.vs_row1_without', 'how_it_works_page.vs_row1_with'],
  ['how_it_works_page.vs_row2_without', 'how_it_works_page.vs_row2_with'],
  ['how_it_works_page.vs_row3_without', 'how_it_works_page.vs_row3_with'],
  ['how_it_works_page.vs_row4_without', 'how_it_works_page.vs_row4_with'],
  ['how_it_works_page.vs_row5_without', 'how_it_works_page.vs_row5_with'],
];

const FAQ_KEYS = [
  ['how_it_works_page.faq_q1', 'how_it_works_page.faq_a1'],
  ['how_it_works_page.faq_q2', 'how_it_works_page.faq_a2'],
  ['how_it_works_page.faq_q3', 'how_it_works_page.faq_a3'],
  ['how_it_works_page.faq_q4', 'how_it_works_page.faq_a4'],
  ['how_it_works_page.faq_q5', 'how_it_works_page.faq_a5'],
  ['how_it_works_page.faq_q6', 'how_it_works_page.faq_a6'],
  ['how_it_works_page.faq_q7', 'how_it_works_page.faq_a7'],
  ['how_it_works_page.faq_q8', 'how_it_works_page.faq_a8'],
];

function FaqItem({ q, a, delay, visible }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{
        background: '#111827',
        border: '1px solid #1E2D45',
        transition: `opacity 0.5s ease ${delay}ms, transform 0.5s ease ${delay}ms`,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(16px)',
      }}
    >
      <button
        className="w-full flex items-center justify-between gap-4 p-5 text-left transition-colors"
        style={{ background: open ? 'rgba(0,213,255,0.04)' : 'transparent' }}
        onClick={() => setOpen(v => !v)}
      >
        <span className="font-heading font-semibold text-sm" style={{ color: '#F8FAFC' }}>{q}</span>
        {open ? <ChevronUp className="w-4 h-4 shrink-0" style={{ color: '#00D5FF' }} /> : <ChevronDown className="w-4 h-4 shrink-0" style={{ color: '#475569' }} />}
      </button>
      <div style={{ maxHeight: open ? '200px' : '0', overflow: 'hidden', transition: 'max-height 0.35s ease' }}>
        <p className="px-5 pb-5 text-sm leading-relaxed" style={{ color: '#94A3B8' }}>{a}</p>
      </div>
    </div>
  );
}

export default function ComoFunciona() {
  const { t } = useI18n();
  const compRef = useRef(null);
  const compVisible = useIntersection(compRef);
  const faqRef = useRef(null);
  const faqVisible = useIntersection(faqRef);

  return (
    <div className="min-h-screen font-body overflow-x-hidden" style={{ background: '#070B14', color: '#F8FAFC' }}>
      <PublicNavbar />

      {/* Hero */}
      <section
        className="relative pt-32 pb-20 px-6 text-center overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #070B14 0%, #0d1525 50%, #111827 100%)' }}
      >
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 50% 60%, rgba(0,213,255,0.1) 0%, transparent 60%)' }} />
        <div className="relative z-10 max-w-3xl mx-auto">
          <h1 className="font-heading font-bold text-white mb-4" style={{ fontSize: 'clamp(1.8rem, 4.5vw, 2.8rem)', lineHeight: 1.2 }}>
            {t('how_it_works_page.title')}
          </h1>
          <p className="text-lg leading-relaxed mb-8" style={{ color: '#94A3B8' }}>
            {t('how_it_works_page.subtitle')}
          </p>
          {/* Animated data flow */}
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 rounded-xl px-4 py-2 border" style={{ background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.12)' }}>
              <span className="text-xl">🟦</span>
              <span className="text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>Meta Ads</span>
            </div>
            <div className="flex items-center gap-1" style={{ animation: 'flowArrow 1.5s ease-in-out infinite', color: '#00D5FF' }}>
              <div className="w-8 h-0.5" style={{ background: 'linear-gradient(90deg, #00D5FF, #7C3AED)' }} />
              <div className="w-0 h-0 border-l-4 border-y-2 border-y-transparent" style={{ borderLeftColor: '#7C3AED' }} />
            </div>
            <div className="flex items-center gap-2 rounded-xl px-4 py-2 border" style={{ background: 'rgba(0,213,255,0.08)', borderColor: 'rgba(0,213,255,0.35)' }}>
              <BarChart2 className="w-4 h-4" style={{ color: '#00D5FF' }} />
              <span className="text-sm font-semibold" style={{ color: '#F8FAFC' }}>Bezkraen</span>
            </div>
            <div className="flex items-center gap-1" style={{ animation: 'flowArrow 1.5s ease-in-out 0.3s infinite', color: '#7C3AED' }}>
              <div className="w-8 h-0.5" style={{ background: 'linear-gradient(90deg, #7C3AED, #22C55E)' }} />
              <div className="w-0 h-0 border-l-4 border-y-2 border-y-transparent" style={{ borderLeftColor: '#22C55E' }} />
            </div>
            <div className="flex items-center gap-2 rounded-xl px-4 py-2 border" style={{ background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.12)' }}>
              <span className="text-xl">📊</span>
              <span className="text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>Insights</span>
            </div>
          </div>
          <style>{`@keyframes flowArrow { 0%,100%{opacity:0.5;transform:translateX(-2px)} 50%{opacity:1;transform:translateX(2px)} }`}</style>
        </div>
      </section>

      {/* Steps zigzag */}
      <section className="py-24 px-6" style={{ background: '#0d1525' }}>
        <div className="max-w-5xl mx-auto space-y-20">
          {STEPS.map((step, i) => (
            <StepRow key={step.n} step={step} i={i} t={t} />
          ))}
        </div>
      </section>

      {/* Comparison table */}
      <section className="py-20 px-6" style={{ background: '#070B14' }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading font-bold text-3xl text-center mb-12" style={{ color: '#F8FAFC' }}>{t('how_it_works_page.vs_title')}</h2>
          <div ref={compRef} className="rounded-3xl overflow-hidden" style={{ border: '1px solid #1E2D45', background: '#111827' }}>
            <div className="grid grid-cols-2" style={{ borderBottom: '1px solid #1E2D45' }}>
              <div className="p-4 text-center" style={{ borderRight: '1px solid #1E2D45' }}>
                <span className="inline-flex items-center gap-2 text-sm font-bold" style={{ color: '#EF4444' }}>
                  <X className="w-4 h-4" /> Sin Bezkraen
                </span>
              </div>
              <div className="p-4 text-center">
                <span className="inline-flex items-center gap-2 text-sm font-bold" style={{ color: '#22C55E' }}>
                  <CheckCircle2 className="w-4 h-4" /> Con Bezkraen
                </span>
              </div>
            </div>
            {COMPARISON_KEYS.map(([withoutKey, withKey], i) => (
              <div
                key={i}
                className="grid grid-cols-2"
                style={{
                  borderBottom: i < COMPARISON_KEYS.length - 1 ? '1px solid rgba(30,45,69,0.5)' : 'none',
                  transition: `opacity 0.5s ease ${i * 100}ms, transform 0.5s ease ${i * 100}ms`,
                  opacity: compVisible ? 1 : 0,
                  transform: compVisible ? 'translateY(0)' : 'translateY(12px)',
                }}
              >
                <div className="p-4 flex items-center gap-3" style={{ borderRight: '1px solid rgba(30,45,69,0.5)' }}>
                  <X className="w-4 h-4 shrink-0" style={{ color: '#EF4444' }} />
                  <span className="text-sm" style={{ color: '#E5E7EB' }}>{t(withoutKey)}</span>
                </div>
                <div className="p-4 flex items-center gap-3">
                  <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: '#22C55E' }} />
                  <span className="text-sm font-medium" style={{ color: '#F8FAFC' }}>{t(withKey)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-6" style={{ background: '#0d1525' }}>
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading font-bold text-3xl text-center mb-12" style={{ color: '#F8FAFC' }}>{t('how_it_works_page.faq_title')}</h2>
          <div ref={faqRef} className="space-y-3">
            {FAQ_KEYS.map(([qKey, aKey], i) => (
              <FaqItem key={i} q={t(qKey)} a={t(aKey)} delay={i * 60} visible={faqVisible} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6" style={{ background: 'linear-gradient(135deg, #070B14, #111827)' }}>
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-heading font-bold text-white text-3xl mb-3">{t('how_it_works_page.cta')}</h2>
          <p className="mb-6" style={{ color: '#94A3B8' }}>{t('landing.pricing_free_trial')}, {t('landing.pricing_no_card').toLowerCase()}</p>
          <button
            onClick={() => base44.auth.redirectToLogin()}
            className="inline-flex items-center gap-2 font-bold px-8 py-3.5 rounded-xl transition-all hover:opacity-90 text-base"
            style={{ background: '#00D5FF', color: '#070B14' }}
          >
            {t('landing.start_free')}
          </button>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}